import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { NotebookEmbedVizResult, NotebookVizRender, NotebookVizState } from '@mode/notebooks/domain';
import { CappieReporterService } from '@mode/shared/util-cappie-reporter';
import { HelpDocsService } from '@mode/shared/util-js';
import { AnsiUp } from 'ansi_up';
import { ProgressEvents } from 'libs/shared/contract-common/src/lib/cappie-reporter.types';
import { isEqual } from 'lodash';
import { Subject } from 'rxjs';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'mode-notebook-cell-renderer',
  templateUrl: './notebook-cell-renderer.component.html',
  styleUrls: ['./notebook-cell-renderer.component.scss'],
})
export class NotebookCellRendererComponent implements OnChanges, OnInit, OnDestroy {
  @Input() cell!: NotebookVizRender;
  @Input() viewer?: string;

  @Output() renderStart = new EventEmitter();
  @Output() renderEnd = new EventEmitter<true | { $err: string }>();

  pythonCell!: NotebookVizRender;

  renderEndEmitValue: true | { $err: string } = true;

  ansi_up = new AnsiUp();

  imageRenderingReporters = new Map<string, ProgressEvents>();

  private cellStateNotifier$: Subject<NotebookVizState> = new Subject();

  private _destroy$ = new Subject<void>();

  constructor(
    private domSanitizer: DomSanitizer,
    private window: Window,
    private cappieReporterService: CappieReporterService,
    public helpDocs: HelpDocsService
  ) {}

  ngOnInit(): void {
    this.cellStateNotifier$
      .pipe(
        takeUntil(this._destroy$),
        distinctUntilChanged(),
        filter((state) =>
          [NotebookVizState.Succeeded, NotebookVizState.Failed, NotebookVizState.Skipped].includes(state)
        )
      )
      .subscribe(() => {
        this.renderEnd.emit(this.renderEndEmitValue);
        this.startTrackingResultsLoad();
      });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['cell']) {
      const { currentValue, previousValue } = changes['cell'];
      // Checking for results was not effective as when python_state == failed,
      // we dont have anything in results to check against
      const needsUpdate =
        changes['cell'].firstChange ||
        !isEqual(currentValue?.state, previousValue?.state) ||
        !isEqual(currentValue?.results, previousValue?.results);

      // Only trigger a renderStart if first cell.change or if the previous value state was not loading
      const needsRenderStart = changes['cell'].firstChange || !this.cellIsLoading(previousValue);

      if (needsUpdate) {
        if (needsRenderStart) {
          this.renderStart.emit();
        }

        const [cell, error] = this.updateAnsiColors(currentValue);
        this.pythonCell = cell;
        this.renderEndEmitValue = error || true;
      }

      this.cellStateNotifier$.next(currentValue?.state);
    }
  }

  downloadCSV(url: string) {
    this.window.open(url, '_blank');
  }

  updateAnsiColors(cell: NotebookVizRender): [NotebookVizRender, { $err: string } | null] {
    if (!cell || !cell.results) {
      return [cell, { $err: 'No cell or cell results' }];
    }
    if (this.cellHasErrorState(cell)) {
      return [cell, { $err: 'Took to long or a problem with the underlying query' }];
    }
    if (this.cellIsLoading(cell)) {
      return [cell, null];
    }
    let error = null;
    cell.results
      .filter((result: NotebookEmbedVizResult) => result.result_type === 'error')
      .forEach((result: NotebookEmbedVizResult) => {
        error = result.content;
        this.ansi_up.use_classes = true;
        result.content = this.ansi_up.ansi_to_html((error as string) || 'There was a problem running this cell');
      });
    return [cell, error ? { $err: error } : null];
  }

  cellHasErrorState(cell: NotebookVizRender) {
    return cell.state === 'failed' || cell.state === 'skipped';
  }

  cellIsLoading(cell: NotebookVizRender) {
    return cell.state === 'loading';
  }

  imageLoaded(result: NotebookEmbedVizResult) {
    this.stopTrackingImageLoad(result);
  }

  startTrackingResultsLoad() {
    this.pythonCell.results.forEach((result) => {
      if (result.result_type === 'image') {
        this.startTrackingImageLoad(result);
      }
    });
  }

  startTrackingImageLoad(result: NotebookEmbedVizResult) {
    const reporter = this.cappieReporterService.progressEventAPI('notebook-image-reporter');
    this.imageRenderingReporters.set(result.token, reporter);
    reporter?.start('starting notebook image loading');
  }

  stopTrackingImageLoad(result: NotebookEmbedVizResult) {
    const reporter = this.imageRenderingReporters.get(result.token);
    if (reporter) {
      reporter.end('finished notebook image loading');
      this.imageRenderingReporters.delete(result.token);
    }
  }
}
